import { api } from './client';
import { useAuthStore } from '@/stores/auth';

export type AttendanceStatus = 'present' | 'absent' | 'late' | 'excused';

// One student's two-signal attendance: did they join the room, and how many
// roll-call rounds did they answer.
export type DoubleAttendanceRow = {
  student_id: string;
  student_name: string;
  status: AttendanceStatus;
  auto_joined: boolean;
  roll_call_total: number;
  roll_call_answered: number;
};

export type AttendanceRecord = {
  id: string;
  student_id: string;
  live_lesson_id: string;
  status: AttendanceStatus;
  method: 'code' | 'manual';
  marked_at: string;
};

export type AttendanceStats = {
  total: number;
  present: number;
  absent: number;
  late: number;
  excused: number;
  percent: number;
};

// Backend AttendanceLog shape (model.AttendanceLog json tags).
type BackendAttendanceLog = {
  id: string;
  live_lesson_id: string;
  student_id: string;
  status: AttendanceStatus;
  marked_at: string;
  marked_by?: string;
  comment?: string;
};

// Adapts a backend AttendanceLog to the frontend AttendanceRecord contract.
// The backend has no "method" field; it stores who marked via marked_by
// (uuid.Nil == all-zeros means a manual mark by a curator with no id, while a
// non-nil marked_by from a code submission also fills it). We can't reliably
// distinguish, so default to 'manual' when a curator comment exists, otherwise
// treat code submissions (status present, no comment) as 'code'.
function toAttendanceRecord(log: BackendAttendanceLog): AttendanceRecord {
  const method: 'code' | 'manual' = log.comment ? 'manual' : 'code';
  return {
    id: log.id,
    student_id: log.student_id,
    live_lesson_id: log.live_lesson_id,
    status: log.status,
    method,
    marked_at: log.marked_at,
  };
}

export const attendanceApi = {
  // ----- Student -----
  async submitCode(code: string) {
    // Backend requires student_id in the body (binding:"required").
    const studentId = useAuthStore.getState().user?.id;
    const { data } = await api.post('/attendance/student/submit-code', {
      code,
      student_id: studentId,
    });
    // Backend returns { status: "marked", lesson_id }. The marked record is
    // always created with status "present", so surface that to the caller.
    const payload = data as { lesson_id: string; status: string };
    return { lesson_id: payload.lesson_id, status: 'present' as AttendanceStatus };
  },
  // Auto check-in when the student joins a live-lesson video room. Marks the
  // authenticated student "present" for the lesson — no attendance code needed.
  async checkinLiveLesson(lessonId: string) {
    await api.post(`/attendance/student/live-lessons/${lessonId}/checkin`);
  },
  // Roll-call (second attendance signal): student confirms a curator's round.
  async answerRollCall(rollcallId: string) {
    await api.post(`/attendance/student/rollcall/${rollcallId}/answer`);
  },
  // Curator triggers a roll-call round in a live lesson.
  async triggerRollCall(lessonId: string, windowSec = 60) {
    const { data } = await api.post(`/attendance/curator/live-lessons/${lessonId}/rollcall`, { window_sec: windowSec });
    return data as { rollcall_id: string; expires_at: string };
  },
  // Curator: two-signal (join + roll-call) attendance summary for a lesson.
  async getDoubleAttendance(lessonId: string) {
    const { data } = await api.get(`/attendance/curator/live-lessons/${lessonId}/double`);
    return data as { rows: DoubleAttendanceRow[]; roll_call_total: number };
  },
  async getMyStats() {
    // Backend requires ?student_id= query param.
    const studentId = useAuthStore.getState().user?.id;
    const { data } = await api.get('/attendance/student/my-stats', {
      params: { student_id: studentId },
    });
    // Backend returns { total, present, records }. Compute the remaining
    // status buckets and percent from the records array.
    const payload = data as {
      total: number;
      present: number;
      records?: BackendAttendanceLog[];
      items?: BackendAttendanceLog[];
    };
    const records = payload.records ?? payload.items ?? [];
    let present = 0;
    let absent = 0;
    let late = 0;
    let excused = 0;
    for (const r of records) {
      switch (r.status) {
        case 'present':
          present++;
          break;
        case 'absent':
          absent++;
          break;
        case 'late':
          late++;
          break;
        case 'excused':
          excused++;
          break;
      }
    }
    const total = payload.total ?? records.length;
    const percent = total > 0 ? Math.round((present / total) * 100) : 0;
    return { total, present, absent, late, excused, percent } as AttendanceStats;
  },

  // ----- Curator -----
  async startAttendance(lessonId: string, durationSeconds: number) {
    const { data } = await api.post(`/attendance/curator/live-lessons/${lessonId}/start`, {
      duration_seconds: durationSeconds,
    });
    // Backend returns { code, lesson_id } and does NOT include expires_at, so
    // derive an approximate expiry from the requested duration.
    const payload = data as { code: string; lesson_id: string };
    const expiresAt = new Date(Date.now() + durationSeconds * 1000).toISOString();
    return { code: String(payload.code), expires_at: expiresAt };
  },
  async stopAttendance(lessonId: string) {
    await api.delete(`/attendance/curator/live-lessons/${lessonId}/stop`);
  },
  async manualMark(lessonId: string, payload: { student_id: string; status: AttendanceStatus; note?: string }) {
    await api.post(`/attendance/curator/live-lessons/${lessonId}/manual`, payload);
  },
  async getLessonAttendance(lessonId: string) {
    const { data } = await api.get(`/attendance/curator/live-lessons/${lessonId}`);
    // Backend returns a bare array of AttendanceLog (no "method" field).
    const records = (data as BackendAttendanceLog[]) ?? [];
    return records.map(toAttendanceRecord);
  },
};
